import { useEffect, useState } from 'preact/hooks';

/**
 * Per-user toggles for the plant schema (DpViewer) visualization. Stored in
 * localStorage so the operator's chosen density sticks across reloads.
 *
 * Kept intentionally coarse — one flag per visual feature — so it's obvious
 * from the settings blob what's on and what's off. New flags append with a
 * sensible default; the merge in `load()` preserves forward compatibility.
 */
export interface SchemaViewSettings {
  /** Subtle horizontal separator lines between leaf rows. */
  rowLines: boolean;
  /** Shine animation on value change. */
  flashOnChange: boolean;
  /** Show unit inline after the numeric value ("42.3 °C"). */
  showUnit: boolean;
  /** Tiny bar to the left of the value showing where it sits within
   *  [MinValue, MaxValue]. Rendered only when the leaf declares both. */
  minMaxBar: boolean;
  /** Compact sparkline of the last few values. Cheap because we cap the
   *  ring buffer inside signalStore. */
  sparkline: boolean;
}

const STORAGE_KEY = 'hv.schema.viewSettings.v1';

const DEFAULTS: SchemaViewSettings = {
  rowLines: false,
  flashOnChange: true,
  showUnit: true,
  minMaxBar: false,
  sparkline: false,
};

function load(): SchemaViewSettings {
  if (typeof window === 'undefined') return { ...DEFAULTS };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw) as Partial<SchemaViewSettings>;
    return { ...DEFAULTS, ...parsed };
  } catch {
    return { ...DEFAULTS };
  }
}

export function useSchemaViewSettings(): [
  SchemaViewSettings,
  <K extends keyof SchemaViewSettings>(key: K, value: SchemaViewSettings[K]) => void,
] {
  const [settings, setSettings] = useState<SchemaViewSettings>(() => load());

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);

  const update = <K extends keyof SchemaViewSettings>(key: K, value: SchemaViewSettings[K]) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  return [settings, update];
}
